// Composer (DESIGN §3.12.6): Pill 48 hoch (wächst bis 8 Zeilen, Radius dann 20), Fläche `composer`
// bzw. `composer-in-card`, hell mit Rand `line`; links „+“ (Anhang), Textfeld, rechts Emoji und
// Senden-Kreis 36 (leer deaktiviert). Über der Pill: Anhang-Chips, „Replying in thread“, Bearbeiten.
// Tastatur: Enter sendet (nicht bei Bildschirmtastatur `pointer: coarse`, nie während IME),
// Shift+Enter Zeile, ↑ im leeren Feld bearbeitet die letzte eigene Nachricht, „@“ öffnet Erwähnungen.
// Größe `lg` (56, Radius 24) mit Stopp-Knopf, Sperr-Hinweis und ⌘⇧C nutzt der PromptComposer (§3.4.2).
import {
  useId,
  useLayoutEffect,
  useRef,
  type KeyboardEvent,
  type ReactNode,
  type Ref,
} from 'react';
import { ArrowUpIcon, PlusIcon, SmileyIcon, StopIcon } from '@phosphor-icons/react';
import { autoGrow } from './_lib/autoGrow';
import { mergeRefs } from './_lib/mergeRefs';
import { useControllable } from './_lib/useControllable';
import { useMentions, type MentionSource } from './_lib/useMentions';
import { IconButton } from './IconButton';
import { MentionPopover, mentionOptionId } from './MentionPopover';
import { Tooltip } from './Tooltip';
import { cn } from '../basis/kern';

export interface ComposerProps {
  ref?: Ref<HTMLTextAreaElement>;
  value?: string;
  defaultValue?: string;
  /** Entwurf merken (je Kanal, SPEC F-chat-03) – der Aufrufer speichert. */
  onValueChange?: (value: string) => void;
  /** Senden; ein abgelehntes Promise stellt den Text wieder her. */
  onSend: (text: string) => void | Promise<void>;
  placeholder?: string;
  /** Name des Textfelds, z. B. „Message #team-credit-cards“. */
  'aria-label': string;
  /** `page` → Fläche `composer`, `card` (Dock) → `composer-in-card`. */
  surface?: 'page' | 'card';
  /** „+“: Anhang hinzufügen (Speicher-Adapter). */
  onAttach?: () => void;
  /** Anhang-Chips über der Pill (FileTile `chip`). */
  attachments?: ReactNode;
  /** Emoji-Knopf; Standard-IconButton, wenn `onEmoji` gesetzt ist. */
  emoji?: ReactNode;
  onEmoji?: () => void;
  /** Hinweis über der Pill, z. B. „Replying in thread“. */
  context?: ReactNode;
  /** Bearbeiten-Modus: Hinweis „Editing message · Esc to cancel“, Esc ruft onCancelEdit. */
  editing?: boolean;
  onCancelEdit?: () => void;
  /** ↑ im leeren Feld: letzte eigene Nachricht bearbeiten. */
  onEditLast?: () => void;
  mentions?: MentionSource;
  /** Kein Post-Recht: ersetzt die Pill durch einen Hinweis (UX CH1). */
  readOnlyReason?: ReactNode;
  /** Senden gesperrt (offline, Upload läuft). */
  sendDisabled?: boolean;
  /** Anhänge zählen als Inhalt (Senden auch ohne Text). */
  hasAttachments?: boolean;
  maxRows?: number;
  /** `md` Chat-Pill 48 · `lg` Bar-AI-Pill 56 mit Radius 24 (PromptComposer). */
  size?: 'md' | 'lg';
  /** Antwort läuft: Stopp-Knopf statt Senden, Esc ruft `onStop`. */
  streaming?: boolean;
  onStop?: () => void;
  /** Pill gesperrt, Hinweis steht in der Pill (z. B. Rate-Limit). */
  lockedReason?: ReactNode;
  /** Grund für gesperrtes Senden als Tooltip am Senden-Knopf (z. B. offline). */
  sendDisabledReason?: string;
  /** ⌘⇧C bzw. Strg+Umschalt+C: letzte Antwort kopieren. */
  onCopyLast?: () => void;
  className?: string;
}

const isCoarsePointer = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches === true;

export function Composer({
  ref,
  value: valueProp,
  defaultValue = '',
  onValueChange,
  onSend,
  placeholder,
  surface = 'page',
  onAttach,
  attachments,
  emoji,
  onEmoji,
  context,
  editing = false,
  onCancelEdit,
  onEditLast,
  mentions,
  readOnlyReason,
  sendDisabled = false,
  hasAttachments = false,
  maxRows = 8,
  size = 'md',
  streaming = false,
  onStop,
  lockedReason,
  sendDisabledReason,
  onCopyLast,
  className,
  ...aria
}: ComposerProps) {
  const inner = useRef<HTMLTextAreaElement>(null);
  const pillRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const [value, setValue] = useControllable(valueProp, defaultValue, onValueChange);
  const placeCaret = (text: string, caret: number) => {
    setValue(text);
    requestAnimationFrame(() => inner.current?.setSelectionRange(caret, caret));
  };
  const mention = useMentions(mentions, placeCaret);
  const blocked = sendDisabled || Boolean(sendDisabledReason) || Boolean(lockedReason) || streaming;
  const canSend = (value.trim().length > 0 || hasAttachments) && !blocked;
  const large = size === 'lg';

  // Höhe und Radius folgen dem Inhalt (auch nach Senden/Entwurf-Wechsel); nur DOM, kein State.
  useLayoutEffect(() => {
    const el = inner.current;
    if (!el) return;
    autoGrow(el, maxRows);
    if (pillRef.current) pillRef.current.dataset.multiline = String(el.offsetHeight > 40);
  }, [value, maxRows]);

  const send = () => {
    if (!canSend) return;
    const text = value;
    setValue('');
    mention.close();
    Promise.resolve(onSend(text.trim())).catch(() => setValue(text));
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.nativeEvent.isComposing || event.keyCode === 229) return;
    if (mention.onKeyDown(event, value)) {
      event.preventDefault();
      return;
    }
    if (
      onCopyLast &&
      (event.metaKey || event.ctrlKey) &&
      event.shiftKey &&
      event.key.toLowerCase() === 'c'
    ) {
      event.preventDefault();
      onCopyLast();
      return;
    }
    if (event.key === 'Enter' && !event.shiftKey && !event.altKey && !isCoarsePointer()) {
      event.preventDefault();
      send();
    } else if (event.key === 'Escape' && streaming && onStop) {
      event.preventDefault();
      onStop();
    } else if (event.key === 'Escape' && editing) {
      event.preventDefault();
      onCancelEdit?.();
    } else if (event.key === 'ArrowUp' && value === '' && onEditLast) {
      event.preventDefault();
      onEditLast();
    }
  };

  if (readOnlyReason) {
    return (
      <div
        role="note"
        className={cn(
          'flex min-h-12 items-center justify-center rounded-full bg-tile px-5 text-center text-label text-fg-secondary',
          className,
        )}
      >
        {readOnlyReason}
      </div>
    );
  }

  const hint = editing ? (
    <span>
      Editing message · <kbd className="font-sans">Esc</kbd> to cancel
    </span>
  ) : (
    context
  );
  return (
    <div className={cn('relative flex flex-col gap-2', className)}>
      {hint ? <div className="px-4 text-label text-fg-secondary">{hint}</div> : null}
      {attachments ? <div className="flex flex-wrap gap-2 px-2">{attachments}</div> : null}
      {mention.open ? (
        <MentionPopover
          id={listId}
          items={mention.items}
          activeIndex={mention.activeIndex}
          onActiveIndexChange={mention.setActiveIndex}
          onSelect={(item) => mention.select(item, value)}
          loading={mention.loading}
        />
      ) : null}
      <div
        ref={pillRef}
        data-multiline="false"
        data-size={size}
        className={cn(
          'flex items-end gap-1 border border-transparent [[data-theme=light]_&]:border-line',
          large ? 'min-h-14 rounded-3xl p-2.5' : 'min-h-12 rounded-full p-1.5',
          'data-[multiline=true]:rounded-2xl',
          'transition-[border-radius] duration-(--bar-dur-fast) ease-standard',
          'focus-within:outline-2 focus-within:outline-offset-0 focus-within:outline-focus focus-within:outline-solid',
          surface === 'page' ? 'bg-composer' : 'bg-composer-in-card',
        )}
      >
        {onAttach ? (
          <IconButton
            label="Add attachment"
            icon={<PlusIcon />}
            variant="ghost"
            surface="tile"
            size="sm"
            disabled={Boolean(lockedReason)}
            onClick={onAttach}
            className={large ? 'size-9' : 'mb-0.5'}
          />
        ) : null}
        {lockedReason ? (
          <p
            role="status"
            className="min-h-9 flex-1 self-center px-2 py-[7px] text-body text-fg-secondary"
          >
            {lockedReason}
          </p>
        ) : (
          <textarea
            ref={mergeRefs(ref, inner)}
            rows={1}
            value={value}
            placeholder={placeholder}
            aria-label={aria['aria-label']}
            aria-autocomplete={mentions ? 'list' : undefined}
            aria-controls={mention.open ? listId : undefined}
            aria-activedescendant={
              mention.open && mention.items.length
                ? mentionOptionId(listId, mention.activeIndex)
                : undefined
            }
            onChange={(event) => {
              setValue(event.target.value);
              mention.update(event.target.value, event.target.selectionStart);
            }}
            onSelect={(event) =>
              mention.update(event.currentTarget.value, event.currentTarget.selectionStart)
            }
            onBlur={() => mention.close()}
            onKeyDown={onKeyDown}
            className={cn(
              'max-h-none min-h-9 flex-1 resize-none bg-transparent px-2 py-[7px] text-body text-fg outline-none',
              'placeholder:truncate placeholder:text-fg-placeholder focus-visible:outline-none',
            )}
          />
        )}
        {emoji ??
          (onEmoji ? (
            <IconButton
              label="Emoji"
              icon={<SmileyIcon />}
              variant="ghost"
              surface="tile"
              size="sm"
              onClick={onEmoji}
              className="mb-0.5"
            />
          ) : null)}
        <SendButton
          streaming={streaming}
          onStop={onStop}
          canSend={canSend}
          reason={sendDisabledReason}
          onSend={send}
        />
      </div>
    </div>
  );
}

/** Senden (Primär, Pfeil) bzw. Stopp (Sekundär, gefülltes Quadrat) während des Streamens. */
function SendButton({
  streaming,
  onStop,
  canSend,
  reason,
  onSend,
}: {
  streaming: boolean;
  onStop?: () => void;
  canSend: boolean;
  reason?: string;
  onSend: () => void;
}) {
  if (streaming) {
    return (
      <IconButton
        label="Stop generating"
        icon={<StopIcon weight="fill" />}
        variant="secondary"
        surface="card"
        size="sm"
        onClick={onStop}
        className="size-9"
      />
    );
  }
  if (reason) {
    // Gesperrt mit Grund: fokussierbar (aria-disabled), damit der Tooltip auch per Tastatur erscheint.
    return (
      <Tooltip content={reason}>
        <IconButton
          label="Send"
          icon={<ArrowUpIcon weight="bold" />}
          variant="primary"
          size="sm"
          tooltip={false}
          aria-disabled
          className="size-9 bg-pill-card! text-fg-disabled!"
        />
      </Tooltip>
    );
  }
  return (
    <IconButton
      label="Send"
      icon={<ArrowUpIcon weight="bold" />}
      variant="primary"
      size="sm"
      disabled={!canSend}
      onClick={onSend}
      className="size-9"
    />
  );
}
